<script setup lang="ts">
import {computed, ref} from 'vue';
import {IconAddFriend, IconCamera, IconPencil} from "~/components/ui/icons";

const {t} = useI18n();
const openedEditModal = ref(false);
const isSectionHidden = ref(false);

function closeEditModal() {
  openedEditModal.value = false;
}

const {profile} = defineProps<{
  profile: any;
}>();

function hideSection() {
  isSectionHidden.value = true;
}

const remainingSteps = computed(() => {
  const steps = [
    {completed: !!profile.avatar, order: 0},
    {completed: !!profile.bio, order: 1},
    {completed: profile.stats.following > 4, order: 2}
  ];

  const incompleteSteps = steps.filter((step) => !step.completed).sort((a, b) => a.order - b.order);
  return incompleteSteps.length;
});
</script>

<template>
  <section v-if="!isSectionHidden && remainingSteps > 0 && profile.is_owner"
           class="max-w-2xl md:px-6 mx-auto pl-3 mb-3.5">
    <div class="flex items-center justify-between py-3">
      <div class="font-semibold max-md:text-xs">{{ t('profile.completion.complete_creation') }}</div>
      <div class="pr-3 lg:pr-0 flex items-center gap-3">
        <span class="whitespace-nowrap max-md:text-xs"> {{ t('profile.completion.left') }} {{ remainingSteps
          }} / 3</span>
        <Button @click="hideSection" variant="outline" class="flex items-center font-semibold">
          <span>{{ t('buttons.hide_section') }}</span>
        </Button>
      </div>
    </div>
    <Modal v-model="openedEditModal">
      <template #dialog-content>
        <DialogContent>
          <DialogHeader>
            <button @click="openedEditModal = false" class="w-auto text-left font-normal text-[17px]" variant="ghost">
              {{ t('buttons.cancel') }}
            </button>
            <h2 class="text-center">{{ t('buttons.edit_profile') }}</h2>
          </DialogHeader>
          <LazyModalsEditProfileModal v-model="profile!" @close="closeEditModal"/>
        </DialogContent>
      </template>
      <template #drawer-content>
        <DrawerContent class="min-h-auto h-auto max-h-dvh">
          <DrawerHeader>{{ t('buttons.edit_profile') }}</DrawerHeader>
          <LazyModalsEditProfileModal v-model="profile!" @close="closeEditModal"/>
        </DrawerContent>
      </template>
    </Modal>
    <div class="flex md:grid md:grid-cols-3 grow scrollbar pr-3 lg:pr-0">
      <div v-if="!profile?.bio" class="min-w-44 basis-1/3 shrink-0 mr-2 snap-start rounded-2xl bg-neutral-100">
        <div class="flex flex-col items-center pt-4 px-3 pb-3 h-full">
          <div class="w-14 h-14 rounded-full flex items-center justify-center bg-background text-foreground">
            <IconPencil size="20px"/>
          </div>
          <h3 class="font-semibold mt-2 mb-4 text-center">{{ t('profile.completion.add_bio') }}</h3>
          <Button variant="outline" class="mt-auto" @click="openedEditModal = true">{{ t('buttons.add') }}</Button>
        </div>
      </div>
      <div v-if="profile?.stats?.following <= 4"
           class="basis-1/3 min-w-44 shrink-0 mr-2 snap-start rounded-2xl bg-neutral-100">
        <div class="flex flex-col items-center pt-4 px-3 pb-3">
          <div class="w-14 h-14 rounded-full flex items-center justify-center bg-background text-foreground">
            <IconAddFriend size="20px"/>
          </div>
          <h3 class="font-semibold mt-2 mb-4 text-center">{{ t('profile.completion.add_followers') }}</h3>
          <Button variant="outline" :to="{ name: 'search' }">{{ t('buttons.find_profiles') }}</Button>
        </div>
      </div>
      <div v-if="!profile?.avatar" class="basis-1/3 min-w-44 shrink-0 snap-start rounded-2xl bg-neutral-100">
        <div class="flex flex-col h-full items-center pt-4 px-3 pb-3">
          <div class="w-14 h-14 rounded-full flex items-center justify-center bg-background text-foreground">
            <IconCamera size="20px"/>
          </div>
          <h3 class="font-semibold mt-2 mb-4 text-center">{{ t('profile.completion.add_photo') }}</h3>
          <Button variant="outline" class="mt-auto" @click="openedEditModal = true">{{ t('buttons.add') }}</Button>
        </div>
      </div>
    </div>
  </section>
</template>
